/** @jsxRuntime classic */
/** @jsx jsx */
import { css, Global, jsx } from "@emotion/core";
import { SETTLEMENT_FEE_DATA } from "constants/purchase";
import { useLiveCommerceShippingFee } from "hooks/Settlement";
import { useLegacyPortalAddressInfo } from "hooks/liveCommerce/useLegacyPortalAddressInfo";
import { cartActions } from "modules/cart";
import { purchaseActions } from "modules/purchase";
import { streamingActions } from "modules/streaming";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { convertMapToValues } from "utility";
import { hasId } from "utility/modelTypeGuards";

import StreamingLiveCommerceCartConfirmV2 from "./liveCommerce/StreamingLiveCommerceCartConfirmV2";
import StreamingLiveCommerceCartV2 from "./liveCommerce/StreamingLiveCommerceCartV2";
import StreamingLiveCommerceDisplayInfo from "./liveCommerce/StreamingLiveCommerceDisplayInfo";
import StreamingLiveCommerceGoodsListV2 from "./liveCommerce/StreamingLiveCommerceGoodsListV2";
import {
  CompletePurchase,
  ErrorModal,
} from "./liveCommerce/StreamingLiveCommerceModal";

import type { PayType } from "@spwn/types";
import type { ProductData } from "@spwn/types/firebase/firestore";
import type { ReqAddToCartAndPurchase } from "modules/cart";

type currentViewType = "goodsList" | "cart" | "goodsInfo" | "confirm";

type Props = {
  currentVideoId: string;
  THEOPlayerElementHeight: number;
};
const StreamingLiveCommerceV2: React.FC<Props> = (props) => {
  const [paymentMethod, setPaymentMethod] = React.useState<PayType>("Card");
  const [settlementFee, setSettlementFee] = React.useState<number>(0);
  const [currentView, setCurrentView] =
    React.useState<currentViewType>("goodsList");
  const [selectedCreditCardSeq, setSelectedCreditCardSeq] = React.useState<
    string | null
  >(null);
  const [selectedSecurityCode, setSelectedSecurityCode] = React.useState<
    string | null
  >(null);

  const [goodsInfo, setGoodsInfo] = React.useState<ProductData | null>(null);

  const eventGoodsMap = useSelector((state) => state.ticket.eventGoodsMap);
  const eventTicketMap = useSelector((state) => state.ticket.eventTicketMap);
  const { cartItemMap, cartTotalNum, isPurchased, errorInfo } = useSelector(
    (state) => state.streaming.liveCommerce
  );
  const displayEvent = useSelector((state) => state.event.displayEvent);
  const isAddressRegistered = useSelector(
    (state) => state.auth.isAddressRegistered
  );
  const registeredCardList = useSelector(
    (state) => state.purchase.cardInfoList
  );
  const { addressInfo, error } =
    useLegacyPortalAddressInfo(isAddressRegistered);

  if (error) {
    console.error("住所の取得に失敗しました:", error);
  }

  const pseudoCart = useMemo(
    () => convertMapToValues(cartItemMap),
    [cartItemMap]
  );

  const dispatch = useDispatch();

  const { isCalculatingShippingFee, shippingFee, isError } =
    useLiveCommerceShippingFee(displayEvent, pseudoCart);

  useEffect(() => {
    dispatch(purchaseActions.refreshFingerprint());
    return () => {
      clearLiveCommerceCart();
      closeErrorModal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const watchSettlementFee = () => {
    setSettlementFee(SETTLEMENT_FEE_DATA[paymentMethod]);
  };
  useEffect(watchSettlementFee, [paymentMethod]);
  useEffect(() => {
    if (
      registeredCardList &&
      registeredCardList.length !== 0 &&
      !selectedCreditCardSeq
    ) {
      setSelectedCreditCardSeq(registeredCardList[0]?.cardSeq ?? null);
    }
  }, [selectedCreditCardSeq, registeredCardList]);

  const mapEventItem = (itemData: ProductData): ProductData | undefined => {
    if (!hasId(itemData)) {
      return;
    }
    const { productType } = itemData;
    if (productType === "ticket") {
      return eventTicketMap[itemData._id];
    }

    if (productType === "goods") {
      return eventGoodsMap[itemData._id];
    }
  };

  const incrementCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }
    dispatch(streamingActions.updateLiveCommerceCart({ item, count: 1 }));
  };

  const decrementCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }

    dispatch(streamingActions.updateLiveCommerceCart({ item, count: -1 }));
  };

  const removeCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }

    dispatch(
      streamingActions.updateLiveCommerceCart({
        item,
        count: 0,
        isRemove: true,
      })
    );
  };

  // TODO 修正必要(?)
  const clearLiveCommerceCart = () => {
    dispatch(purchaseActions.refreshFingerprint());
    dispatch(streamingActions.successPurchaseLiveCommerce(false));
    setCurrentView("goodsList");
  };

  const closeErrorModal = () => {
    dispatch(
      streamingActions.toggleLiveCommerceError({
        isError: false,
        caption: "",
        msg: "",
      })
    );
  };

  const openGoodsInfo = (goodsItem: ProductData) => {
    setCurrentView("goodsInfo");
    setGoodsInfo(goodsItem);
  };

  const closeGoodsInfo = () => {
    setCurrentView("goodsList");
    setGoodsInfo(null);
  };

  const handlePurchase = () => {
    if (pseudoCart.length === 0) {
      console.error("カートが空です");
      return;
    }
    const purchaseItem = pseudoCart.map((item) => ({
      id: item._id,
      productType: item.productType,
      count: item.count,
    }));
    switch (paymentMethod) {
      case "Card": {
        if (!selectedCreditCardSeq || !selectedSecurityCode) {
          console.error("クレジットカードが選択されていません");
          return;
        }
        const cardSeq = Number(selectedCreditCardSeq);
        dispatch(
          cartActions.addToCartAndPurchase.started({
            eventId: displayEvent.eventId,
            currentVideoId: props.currentVideoId,
            products: purchaseItem,
            displayPrice: totalFee,
            cardSeq,
            securityCode: selectedSecurityCode,
            purchaseMethod: "Card",
            source: "liveCommerce",
          } as ReqAddToCartAndPurchase<"Card">)
        );
        break;
      }

      case "CVS":
        //TODO: コンビニ決済処理を実装
        break;
      case "Phone":
        //TODO: キャリア決済処理を実装
        break;
      default:
        console.error("支払い方法が不正です");
        return;
    }
  };

  const cartTotalFee = pseudoCart.reduce(
    (sum, item) => sum + item.price_jpy * item.count,
    0
  );

  const totalFee = cartTotalFee + shippingFee + settlementFee;

  if (!eventGoodsMap) {
    return null;
  }

  if (errorInfo.isError) {
    return (
      <ErrorModal
        caption={errorInfo.caption}
        msg={errorInfo.msg}
        closeModal={closeErrorModal}
      />
    );
  }

  if (isPurchased) {
    return <CompletePurchase clearLiveCommerceCart={clearLiveCommerceCart} />;
  }

  if (currentView === "goodsInfo" && goodsInfo) {
    return (
      <StreamingLiveCommerceDisplayInfo
        goodsInfo={goodsInfo}
        THEOPlayerElementHeight={props.THEOPlayerElementHeight}
        closeGoodsInfo={closeGoodsInfo}
      />
    );
  }

  return (
    <React.Fragment>
      <Global styles={liveCommerceModalOverrideStyles} />

      {currentView === "goodsList" && (
        <StreamingLiveCommerceGoodsListV2
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          itemNum={cartTotalNum}
          pseudoCart={pseudoCart}
          openGoodsInfo={openGoodsInfo}
          openLiveCommerceCart={() => setCurrentView("cart")}
          incrementCartItem={incrementCartItem}
        />
      )}

      {currentView === "cart" && (
        <StreamingLiveCommerceCartV2
          currentVideoId={props.currentVideoId}
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          pseudoCart={pseudoCart}
          paymentMethod={paymentMethod}
          shippingFee={shippingFee}
          cartTotalFee={cartTotalFee}
          totalFee={totalFee}
          settlementFee={settlementFee}
          isCalculatingShippingFee={isCalculatingShippingFee}
          isError={isError}
          selectedCreditCardSeq={selectedCreditCardSeq}
          selectedSecurityCode={selectedSecurityCode}
          addressInfo={addressInfo}
          incrementCartItem={incrementCartItem}
          decrementCartItem={decrementCartItem}
          removeCartItem={removeCartItem}
          closeLiveCommerceCart={() => setCurrentView("goodsList")}
          proceedToCheckout={() => setCurrentView("confirm")}
          setPaymentMethod={setPaymentMethod}
          setSettlementFee={setSettlementFee}
          setSelectedCreditCardSeq={setSelectedCreditCardSeq}
          setSelectedSecurityCode={setSelectedSecurityCode}
        />
      )}

      {currentView === "confirm" && (
        <StreamingLiveCommerceCartConfirmV2
          playerHeight={props.THEOPlayerElementHeight}
          addressInfo={addressInfo}
          paymentMethod={paymentMethod}
          pseudoCart={pseudoCart}
          displayEvent={displayEvent}
          cartTotalFee={cartTotalFee}
          shippingFee={shippingFee}
          settlementFee={settlementFee}
          totalFee={totalFee}
          handleBackToCart={() => setCurrentView("cart")}
          handlePurchase={handlePurchase}
        />
      )}
    </React.Fragment>
  );
};

// TODO: global 経由でのモーダルスタイル注入をしている。適切な方法を考えたい
const liveCommerceModalOverrideStyles = css`
  .loading_layout {
    display: flex;
    align-items: center;
    height: 100% !important;
    margin: 0px auto 0 !important;
    padding: 0px !important;
    background-color: initial !important;
    &:focus {
      outline: none;
    }
    .sk-cube-grid {
      margin: 0 auto !important;
    }
    .sk-cube {
      background-color: #ffffff !important;
    }
    h2 {
      display: none !important;
    }
    .popup_message {
      display: none !important;
    }
  }
`;

export default StreamingLiveCommerceV2;
